import Phaser from 'phaser';
import { TILE_SIZE, MAP_COLS, MAP_ROWS, BASE_VISION_RADIUS, FOG_PENUMBRA_RADIUS } from '@/config/GameConfig';
import { BASE_POSITIONS } from '@/map/MapData';
import { Robot } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { Actor } from '@/entities/Actor';
import { GOD } from '@/config/GodMode';

/** Gradient texture size (px) — higher = smoother falloff */
const GRADIENT_SIZE = 256;
const GRADIENT_KEY = '__fog_gradient__';

/** How long (seconds) a revealed area stays visible after a unit leaves */
const REVEAL_LINGER = 5;
/** How long (seconds) the fade-out takes after linger expires */
const REVEAL_FADE = 3;
/** Maximum number of non-permanent reveal sources (prevents memory leak) */
const MAX_REVEAL_SOURCES = 200;

/** Fog state per tile (for placement logic only) */
const enum FogState {
  UNEXPLORED = 0,
  PENUMBRA = 1,
  EXPLORED = 2,
}

interface RevealSource {
  x: number;
  y: number;
  radius: number;
  /** Time remaining before fade starts (seconds). -1 = permanent (base) */
  linger: number;
  /** Current opacity (1 = full reveal, fades to 0) */
  opacity: number;
}

/**
 * Fog of War with smooth radial gradients and timed reveal decay:
 * - Robot-revealed areas linger for a few seconds then fade back to fog
 * - Base vision is permanent
 * - Tile grid kept for placement validation (isTileVisible)
 * - Robots with direction component have conical vision (fog + radar)
 * - All robots detect enemies within 1 diagonal tile distance (no sensor needed)
 */
export class FogOfWarSystem {
  private scene: Phaser.Scene;
  private fogRT: Phaser.GameObjects.RenderTexture;
  private stamp: Phaser.GameObjects.Image;

  /** Reusable graphics object for drawing cone shapes */
  private coneGfx: Phaser.GameObjects.Graphics;

  /** Pre-baked cone textures keyed by "halfAngle_radius" for fast rotation-based erase */
  private coneStamps: Map<string, Phaser.GameObjects.Image> = new Map();

  /** Tile-level fog state for placement logic */
  private fogState: FogState[][] = [];
  /** Frame counter for throttling expensive updates */
  private frameCount = 0;

  /** Reveal sources with linger timers */
  private revealSources: RevealSource[] = [];
  /** Fast lookup: tileKey → index in revealSources (avoids O(n) find per addRevealSource) */
  private sourceIndex: Map<number, number> = new Map();

  /** Tile keys currently being refreshed by a live robot (skip decay) */
  private activeKeys = new Set<number>();

  /** Scale factor for fog RT: 1 = full-res, 2 = half-res (large maps) */
  private fogScale: number;
  /** Inverse of fogScale for division avoidance */
  private invFogScale: number;

  /** Dirty tiles that need reset on next rebuildFogState (avoids resetting all 10k tiles) */
  private dirtyTiles: Set<number> = new Set();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    this.createGradientTexture();

    const mapW = MAP_COLS * TILE_SIZE;
    const mapH = MAP_ROWS * TILE_SIZE;

    // Half-res RT for large maps (>2000px in either dimension = 50+ tiles)
    this.fogScale = Math.max(mapW, mapH) > 2000 ? 2 : 1;
    this.invFogScale = 1 / this.fogScale;

    this.fogRT = scene.add.renderTexture(0, 0, Math.ceil(mapW * this.invFogScale), Math.ceil(mapH * this.invFogScale));
    this.fogRT.setOrigin(0, 0);
    this.fogRT.setDepth(5);
    this.fogRT.setScale(this.fogScale);
    this.fogRT.fill(0x000000, 1.0);

    this.stamp = scene.add.image(0, 0, GRADIENT_KEY).setVisible(false);
    this.coneGfx = scene.add.graphics().setVisible(false);

    // Initialize fog grid
    this.fogState = [];
    for (let r = 0; r < MAP_ROWS; r++) {
      this.fogState[r] = [];
      for (let c = 0; c < MAP_COLS; c++) {
        this.fogState[r][c] = FogState.UNEXPLORED;
      }
    }

    // Reveal tiles around base positions (permanent)
    const baseRadius = BASE_VISION_RADIUS * TILE_SIZE;
    for (const bp of BASE_POSITIONS) {
      this.addRevealSource(bp.x, bp.y, baseRadius, true);
    }

    // Draw fog immediately so the first frame is correct (no 5-frame black flash)
    this.rebuildFogState();
    this.drawFog([]);
  }

  update(robots: Robot[], enemies: Enemy[], delta: number, enemyBases?: Actor[]): void {
    this.frameCount++;
    const dt = delta / 1000;
    this.updateExplored(robots);
    this.decayRevealSources(dt);
    // Throttle expensive operations: fog grid + render every 6 frames
    // Enemy visibility every 3 frames
    if (this.frameCount % 3 === 0) {
      this.updateEnemyVisibility(robots, enemies);
      if (enemyBases) this.updateBaseVisibility(robots, enemyBases);
    }
    if (this.frameCount % 6 === 0) {
      this.rebuildFogState();
      this.drawFog(robots);
    }
  }

  /** Can the player place a unit on this tile? Explored or penumbra (partially visible) tiles are valid. */
  isTileVisible(col: number, row: number): boolean {
    if (row < 0 || row >= MAP_ROWS || col < 0 || col >= MAP_COLS) return false;
    return this.fogState[row][col] !== FogState.UNEXPLORED;
  }

  /** Is a world-space position visible (not in unexplored fog)? */
  isPositionVisible(px: number, py: number): boolean {
    const col = Math.floor(px / TILE_SIZE);
    const row = Math.floor(py / TILE_SIZE);
    return this.isTileVisible(col, row);
  }

  /** Bounding box (in pixels) of all revealed/penumbra tiles. Returns null if nothing revealed. */
  getRevealedBounds(): { x: number; y: number; w: number; h: number } | null {
    let minC = MAP_COLS, maxC = -1, minR = MAP_ROWS, maxR = -1;
    for (let r = 0; r < MAP_ROWS; r++) {
      for (let c = 0; c < MAP_COLS; c++) {
        if (this.fogState[r][c] !== FogState.UNEXPLORED) {
          if (c < minC) minC = c;
          if (c > maxC) maxC = c;
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
        }
      }
    }
    if (maxC < 0) return null;
    return {
      x: minC * TILE_SIZE,
      y: minR * TILE_SIZE,
      w: (maxC - minC + 1) * TILE_SIZE,
      h: (maxR - minR + 1) * TILE_SIZE,
    };
  }

  /** Return fog exploration stats for scoring (called once at mission end) */
  getExploredStats(): { explored: number; total: number } {
    let explored = 0;
    let total = 0;
    for (let r = 0; r < MAP_ROWS; r++) {
      for (let c = 0; c < MAP_COLS; c++) {
        // Count all non-wall tiles as explorable
        total++;
        if (this.fogState[r]?.[c] !== FogState.UNEXPLORED) {
          explored++;
        }
      }
    }
    return { explored, total };
  }

  // ── Explored state ──

  private updateExplored(robots: Robot[]): void {
    this.activeKeys.clear();
    for (const robot of robots) {
      if (!robot.alive) continue;
      const key = this.tileKey(robot.x, robot.y);
      this.activeKeys.add(key);
      // Fog reveal radius = robot's actual sensor range (stacked), minimum = close-range detection
      const revealRadius = Math.max(robot.sensorRange, GOD.eyesightRange);
      this.addRevealSource(robot.x, robot.y, revealRadius, false);
    }
  }

  private tileKey(px: number, py: number): number {
    return Math.floor(py / TILE_SIZE) * 10000 + Math.floor(px / TILE_SIZE);
  }

  /** Immediately remove all non-permanent reveal sources at a given world position */
  removeSourcesAt(px: number, py: number): void {
    const key = this.tileKey(px, py);
    let removed = false;
    for (let i = this.revealSources.length - 1; i >= 0; i--) {
      const src = this.revealSources[i];
      if (src.linger === -1) continue; // keep permanent (base)
      if (this.tileKey(src.x, src.y) === key) {
        this.revealSources.splice(i, 1);
        removed = true;
      }
    }
    if (removed) this.rebuildSourceIndex();
  }

  /** Add or refresh a reveal source (O(1) via sourceIndex map) */
  private addRevealSource(px: number, py: number, radiusPx: number, permanent: boolean): void {
    const key = this.tileKey(px, py);

    const idx = this.sourceIndex.get(key);
    if (idx !== undefined && idx < this.revealSources.length) {
      const existing = this.revealSources[idx];
      if (this.tileKey(existing.x, existing.y) === key) {
        existing.x = px;
        existing.y = py;
        existing.radius = Math.max(existing.radius, radiusPx);
        existing.linger = permanent ? -1 : REVEAL_LINGER;
        existing.opacity = 1;
        return;
      }
    }

    // Evict oldest non-permanent source if at capacity
    if (!permanent && this.revealSources.length >= MAX_REVEAL_SOURCES) {
      for (let i = 0; i < this.revealSources.length; i++) {
        if (this.revealSources[i].linger !== -1) {
          const oldKey = this.tileKey(this.revealSources[i].x, this.revealSources[i].y);
          this.sourceIndex.delete(oldKey);
          this.revealSources.splice(i, 1);
          // Rebuild index after splice (indices shifted)
          this.rebuildSourceIndex();
          break;
        }
      }
    }

    const newIdx = this.revealSources.length;
    this.revealSources.push({
      x: px, y: py, radius: radiusPx,
      linger: permanent ? -1 : REVEAL_LINGER,
      opacity: 1,
    });
    this.sourceIndex.set(key, newIdx);
  }

  /** Rebuild sourceIndex from revealSources array (after splice) */
  private rebuildSourceIndex(): void {
    this.sourceIndex.clear();
    for (let i = 0; i < this.revealSources.length; i++) {
      const key = this.tileKey(this.revealSources[i].x, this.revealSources[i].y);
      this.sourceIndex.set(key, i);
    }
  }

  /** Decay reveal sources: count down linger, then fade opacity */
  private decayRevealSources(dt: number): void {
    let needsReindex = false;
    for (let i = this.revealSources.length - 1; i >= 0; i--) {
      const src = this.revealSources[i];

      // Permanent sources (base) never decay
      if (src.linger === -1) continue;

      // If a live robot is on this tile, refresh it
      const key = this.tileKey(src.x, src.y);
      if (this.activeKeys.has(key)) {
        src.linger = REVEAL_LINGER;
        src.opacity = 1;
        continue;
      }

      // Count down linger
      if (src.linger > 0) {
        src.linger -= dt;
        continue;
      }

      // Fade out
      src.opacity -= dt / REVEAL_FADE;
      if (src.opacity <= 0) {
        this.sourceIndex.delete(key);
        this.revealSources.splice(i, 1);
        needsReindex = true;
      }
    }
    if (needsReindex) this.rebuildSourceIndex();
  }

  /** Rebuild tile fog state from current reveal sources.
   *  Uses dirty-flag: only resets tiles that were previously marked, then re-marks from sources. */
  private rebuildFogState(): void {
    // Reset only previously-dirty tiles (instead of all MAP_ROWS × MAP_COLS)
    for (const key of this.dirtyTiles) {
      const r = (key / MAP_COLS) | 0;
      const c = key % MAP_COLS;
      this.fogState[r][c] = FogState.UNEXPLORED;
    }
    this.dirtyTiles.clear();

    // Re-apply all active sources, tracking which tiles we touch
    for (const src of this.revealSources) {
      if (src.opacity <= 0) continue;
      const col = Math.floor(src.x / TILE_SIZE);
      const row = Math.floor(src.y / TILE_SIZE);
      const exploreTiles = Math.ceil(src.radius / TILE_SIZE);
      const penumbraTiles = exploreTiles + FOG_PENUMBRA_RADIUS;

      for (let dr = -penumbraTiles; dr <= penumbraTiles; dr++) {
        for (let dc = -penumbraTiles; dc <= penumbraTiles; dc++) {
          const r = row + dr;
          const c = col + dc;
          if (r < 0 || r >= MAP_ROWS || c < 0 || c >= MAP_COLS) continue;
          const distSq = dr * dr + dc * dc;

          if (distSq <= exploreTiles * exploreTiles) {
            this.fogState[r][c] = FogState.EXPLORED;
            this.dirtyTiles.add(r * MAP_COLS + c);
          } else if (distSq <= penumbraTiles * penumbraTiles) {
            if (this.fogState[r][c] === FogState.UNEXPLORED) {
              this.fogState[r][c] = FogState.PENUMBRA;
            }
            this.dirtyTiles.add(r * MAP_COLS + c);
          }
        }
      }
    }
  }

  // ── Vision sources & visibility computation ──

  /** Vision source descriptor used by both enemy and base visibility checks.
   *  Penumbra matches the fog gradient zone so enemies fade with the fog. */
  private static readonly PENUMBRA_PX = FOG_PENUMBRA_RADIUS * TILE_SIZE;

  /**
   * Build an array of vision sources from all alive robots + player base positions.
   * When `markEyesight` is true, non-sensor robots are flagged as binary eyesight
   * sources (no fade margin). Otherwise every source uses gradual fade.
   */
  private buildVisionSources(robots: Robot[], markEyesight: boolean):
    { x: number; y: number; range: number; heading: number; halfAngle: number; isSensor: boolean }[] {

    const sources: { x: number; y: number; range: number; heading: number; halfAngle: number; isSensor: boolean }[] = [];

    for (const robot of robots) {
      if (!robot.alive) continue;

      // Close-range eyesight (full 360°, 1 diagonal tile) — always present
      sources.push({
        x: robot.x, y: robot.y, range: GOD.eyesightRange,
        heading: 0, halfAngle: Math.PI,
        isSensor: !markEyesight ? true : false,
      });

      if (robot.sensorRange > 0) {
        // Directional radar: use sweep angle for fog cone direction
        const fogHeading = robot.hasDirectionComponent
          ? robot.heading + robot.sweepAngle
          : robot.heading;
        sources.push({
          x: robot.x, y: robot.y, range: robot.sensorRange,
          heading: fogHeading, halfAngle: robot.sensorHalfAngle,
          isSensor: true,
        });
      }
    }

    // Player base vision — include penumbra so it matches the fog erase radius
    const baseRange = (BASE_VISION_RADIUS + FOG_PENUMBRA_RADIUS) * TILE_SIZE;
    for (const bp of BASE_POSITIONS) {
      sources.push({ x: bp.x, y: bp.y, range: baseRange, heading: 0, halfAngle: Math.PI, isSensor: true });
    }

    return sources;
  }

  /** Fog fade extension ratio — must match the 15% overshoot used by eraseCircle / getConeStamp */
  private static readonly FADE_OVERSHOOT = 0.15;

  /**
   * Compute the best visibility value (0..1) for a world position given
   * a set of vision sources.
   *  - Inside radar range: α = 1 (fully visible)
   *  - Beyond radar, within the fog fade zone (+15%): α fades 1→0 matching the fog gradient
   *  - Beyond fade zone: α = 0
   * Note: visibility is purely visual — weapons still fire at detected enemies (heuristic).
   */
  private computeVisibilityAt(
    px: number,
    py: number,
    visionSources: { x: number; y: number; range: number; heading: number; halfAngle: number; isSensor: boolean }[],
  ): number {
    let bestVisibility = 0;

    for (const src of visionSources) {
      const dx = px - src.x;
      const dy = py - src.y;
      const distSq = dx * dx + dy * dy;

      // Fade zone extends beyond radar range
      const fadeEnd = src.range * (1 + FogOfWarSystem.FADE_OVERSHOOT);

      // Early reject: beyond fade zone entirely
      if (distSq > fadeEnd * fadeEnd) continue;

      // Check cone angle (skip if outside cone; halfAngle === PI = full 360°)
      if (src.halfAngle < Math.PI) {
        const angle = Math.atan2(dy, dx);
        let diff = angle - src.heading;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        if (Math.abs(diff) > src.halfAngle) continue;
      }

      // Inside radar range → fully visible
      if (distSq <= src.range * src.range) return 1;

      // In the fade zone beyond radar: gradual fade correlated with fog
      const dist = Math.sqrt(distSq);
      const t = 1 - (dist - src.range) / (fadeEnd - src.range);
      bestVisibility = Math.max(bestVisibility, t);
    }

    return bestVisibility;
  }

  // ── Enemy visibility (radar layer) ──

  private updateEnemyVisibility(robots: Robot[], enemies: Enemy[]): void {
    // markEyesight = true: non-sensor robots use binary detection (no fade)
    const visionSources = this.buildVisionSources(robots, true);

    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      if (enemy.fogImmune) continue; // cinematic demo enemies — always visible
      const ex = enemy.body.x;
      const ey = enemy.body.y;

      // First check cone-based vision sources (radar + eyesight + base)
      let vis = this.computeVisibilityAt(ex, ey, visionSources);

      // If not seen by cones, check circular reveal sources (fog-cleared areas).
      // An enemy standing in a fog-cleared zone should be partially visible
      // (the player can see the terrain there, so the enemy shouldn't be fully hidden).
      if (vis < 1) {
        for (const src of this.revealSources) {
          if (src.opacity <= 0) continue;
          const dx = ex - src.x;
          const dy = ey - src.y;
          const distSq = dx * dx + dy * dy;
          const r = src.radius * 1.0; // match the reveal circle radius
          if (distSq <= r * r) {
            // Seen in revealed fog area — use reveal opacity as visibility
            vis = Math.max(vis, src.opacity);
            if (vis >= 1) break;
          }
        }
      }

      enemy.setFogVisibility(vis);
    }
  }

  // ── Enemy base visibility (fog hides HP bars + icon) ──

  private updateBaseVisibility(robots: Robot[], bases: Actor[]): void {
    // markEyesight = false: all sources use gradual fade (uniform treatment)
    const visionSources = this.buildVisionSources(robots, false);

    for (const base of bases) {
      if (!base.alive) continue;
      base.setFogVisibility(this.computeVisibilityAt(base.x, base.y, visionSources));
    }
  }

  // ── Rendering ──

  private drawFog(robots: Robot[]): void {
    this.fogRT.fill(0x000000, 1.0);

    // Viewport bounds for clipping (skip erase ops that are fully off-screen)
    const cam = this.scene.cameras.main;
    const margin = Math.max(cam.width, cam.height); // generous margin for smooth scrolling
    const vpLeft = cam.scrollX - margin;
    const vpRight = cam.scrollX + cam.width + margin;
    const vpTop = cam.scrollY - margin;
    const vpBottom = cam.scrollY + cam.height + margin;

    // Erase decaying reveal sources (viewport-clipped)
    for (const src of this.revealSources) {
      if (src.opacity <= 0) continue;
      if (src.x + src.radius < vpLeft || src.x - src.radius > vpRight ||
          src.y + src.radius < vpTop || src.y - src.radius > vpBottom) continue;
      this.eraseCircle(src.x, src.y, src.radius, src.opacity);
    }

    // Erase around live robots (viewport-clipped)
    for (const robot of robots) {
      if (!robot.alive) continue;
      const maxR = Math.max(robot.sensorRange, GOD.eyesightRange) + FogOfWarSystem.PENUMBRA_PX;
      if (robot.x + maxR < vpLeft || robot.x - maxR > vpRight ||
          robot.y + maxR < vpTop || robot.y - maxR > vpBottom) continue;

      this.eraseCircle(robot.x, robot.y, GOD.eyesightRange, 1);

      if (robot.sensorRange > 0) {
        const heading = robot.hasDirectionComponent
          ? robot.heading + robot.sweepAngle
          : robot.heading;
        this.eraseCone(robot.x, robot.y, robot.sensorRange, heading, robot.sensorHalfAngle, 1);
      }
    }

    // Erase around base positions (always visible, usually on-screen)
    const baseVisRadius = (BASE_VISION_RADIUS + FOG_PENUMBRA_RADIUS) * TILE_SIZE;
    for (const bp of BASE_POSITIONS) {
      this.eraseCircle(bp.x, bp.y, baseVisRadius, 1);
    }
  }

  /** Erase a soft gradient circle from the fog RT (coordinates scaled for half-res).
   *  Stamp is 15% larger than nominal radius so the fade extends BEYOND the range boundary. */
  private eraseCircle(cx: number, cy: number, radius: number, opacity: number): void {
    const effectiveRadius = radius * 1.15; // overshoot: solid=radius, fade=beyond
    const diameter = effectiveRadius * 2 * this.invFogScale;
    const scale = diameter / GRADIENT_SIZE;
    this.stamp.setScale(scale);
    this.stamp.setAlpha(opacity);
    this.fogRT.erase(this.stamp, cx * this.invFogScale, cy * this.invFogScale);
  }

  /** Erase a cone from the fog RT using pre-baked rotatable texture (scaled for half-res) */
  private eraseCone(cx: number, cy: number, radius: number, heading: number, halfAngle: number, opacity: number): void {
    const stamp = this.getConeStamp(radius, halfAngle);
    stamp.setRotation(heading);
    stamp.setAlpha(opacity);
    // Scale the cone stamp down by fogScale for half-res RT
    if (this.fogScale > 1) {
      stamp.setScale(this.invFogScale);
    }
    this.fogRT.erase(stamp, cx * this.invFogScale, cy * this.invFogScale);
    if (this.fogScale > 1) {
      stamp.setScale(1); // restore for next use
    }
  }


  /** Get or create a pre-baked cone texture for a given halfAngle + radius combination */
  private getConeStamp(radius: number, halfAngle: number): Phaser.GameObjects.Image {
    // Quantize to avoid too many unique textures (round radius to 10px, halfAngle to 0.1 rad)
    const qRadius = Math.round(radius / 10) * 10;
    const qHalf = Math.round(halfAngle * 10) / 10;
    const key = `__fog_cone_${qRadius}_${qHalf}__`;

    let img = this.coneStamps.get(key);
    if (img) return img;

    // Bake the cone into a canvas texture (drawn once, reused by rotation)
    // Fade starts INSIDE the radar range and ends AT the radar edge (no overshoot)
    const fadeZone = qRadius * 0.15; // fade extends BEYOND radar edge
    const totalRadius = qRadius + fadeZone; // overshoot: solid=radar, fade=beyond
    const size = Math.ceil(totalRadius * 2);
    const half = size / 2;

    if (!this.scene.textures.exists(key)) {
      const canvas = this.scene.textures.createCanvas(key, size, size)!;
      const ctx = canvas.getContext();

      // Draw cone pointing RIGHT (heading=0) — rotation applied at erase time
      const steps = 24;
      const rings = 8;
      const solidRadius = qRadius; // solid zone = full radar range, fade is beyond
      for (let ring = rings; ring >= 1; ring--) {
        const outerR = totalRadius * (ring / rings);
        const innerR = totalRadius * ((ring - 1) / rings);
        const midR = (outerR + innerR) / 2;
        let alpha: number;
        if (midR <= solidRadius) {
          alpha = 1;
        } else {
          alpha = Math.max(0, 1 - (midR - solidRadius) / fadeZone);
        }
        if (alpha <= 0) continue;

        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(half + Math.cos(-qHalf) * innerR, half + Math.sin(-qHalf) * innerR);
        for (let i = 0; i <= steps; i++) {
          const a = -qHalf + (2 * qHalf) * (i / steps);
          ctx.lineTo(half + Math.cos(a) * outerR, half + Math.sin(a) * outerR);
        }
        for (let i = steps; i >= 0; i--) {
          const a = -qHalf + (2 * qHalf) * (i / steps);
          ctx.lineTo(half + Math.cos(a) * innerR, half + Math.sin(a) * innerR);
        }
        ctx.closePath();
        ctx.fill();
      }
      canvas.refresh();
    }

    img = this.scene.add.image(0, 0, key).setVisible(false);
    this.coneStamps.set(key, img);
    return img;
  }


  // ── Gradient texture generation ──

  private createGradientTexture(): void {
    if (this.scene.textures.exists(GRADIENT_KEY)) return;

    const canvas = this.scene.textures.createCanvas(GRADIENT_KEY, GRADIENT_SIZE, GRADIENT_SIZE)!;
    const ctx = canvas.getContext();
    const half = GRADIENT_SIZE / 2;

    const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
    // Solid until 87% of texture, fade 87→100% (the stamp is scaled 15% larger
    // than the nominal range so the solid zone matches the actual range boundary)
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.87, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.93, 'rgba(255,255,255,0.4)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, GRADIENT_SIZE, GRADIENT_SIZE);
    canvas.refresh();
  }

  shutdown(): void {
    this.fogRT.destroy();
    this.stamp.destroy();
    this.coneGfx.destroy();
    for (const img of this.coneStamps.values()) img.destroy();
    this.coneStamps.clear();
    this.fogState = [];
    this.revealSources = [];
    this.sourceIndex.clear();
    this.dirtyTiles.clear();
  }
}
